// EN/FR copy for the executive (CEO) dashboard. The dashboard root is marked
// data-cvi-i18n="managed" so the legacy DOM translator leaves this text alone.

const plural = (n, one, many) => (n === 1 ? one : many);

const EN = {
    eyebrow: "Executive overview",
    title: "Organization performance",
    subtitle: (people, units) => `${people} people in scope · ${units} top-level ${plural(units, "unit", "units")}`,
    periodLabel: "Evaluation period",
    periodStates: { final: "Finalized", provisional: "In progress", draft: "Not started" },
    banner: {
        provisionalTitle: "Provisional results",
        provisional: (withCvi, total) => `This period is still open. ${withCvi} of ${total} people currently have a provisional CVI; values will change as evaluations are completed and are final only once the period is locked.`,
        finalTitle: "Finalized results",
        final: version => `Values come from the final snapshots stored when this period was locked${version ? ` (formula ${version})` : ""}. They are not recalculated.`,
        errorTitle: "Results could not be loaded",
        error: "The CVI service did not respond for this period. No values are shown rather than incomplete ones.",
        forbidden: "Your account is not authorized to view organization-wide results.",
        retry: "Try again",
        loading: "Loading organization results…",
    },
    hero: {
        title: "Average CVI",
        caption: "Average of available individual CVI results",
        basis: (n, total) => `Based on ${n} of ${total} people`,
        noScore: "Not available",
        noScoreHelp: "No person in scope has a CVI result for this period yet. A score appears once both contribution dimensions have been evaluated.",
        vs: label => `vs ${label} (finalized)`,
        noBaseline: "No earlier finalized period to compare with",
        individual: "Individual Contribution",
        individualHelp: "Responsibilities, activities, goals and peer feedback",
        collective: "Collective Contribution",
        collectiveHelp: "Team, unit and organization indicators",
        formula: "Each person's CVI is the geometric mean of their Individual and Collective Contribution, using the weights configured for their position.",
        provisionalComparison: "Provisional value compared with a finalized one.",
        comparisonWithheld: "Comparison with finalized periods is shown once at least 80% of this period’s assessment weight has been evaluated.",
        cycles: "Average CVI by period",
    },
    attention: {
        title: "Executive attention",
        subtitle: "Prioritized from this period's results",
        empty: "Nothing requires executive attention for this period.",
        severities: { critical: "Priority", warning: "Review", info: "Note" },
        items: {
            load_failed: () => ({ title: "Results unavailable", detail: "The CVI service could not return this period's results." }),
            kpi_critical: item => ({ title: `${item.count} strategic ${plural(item.count, "indicator", "indicators")} below 65% achievement`, detail: item.names.join(" · ") }),
            org_decline: item => ({ title: `Average CVI down ${Math.abs(item.value).toFixed(1)} points`, detail: "Compared with the previous finalized period." }),
            org_improve: item => ({ title: `Average CVI up ${item.value.toFixed(1)} points`, detail: "Compared with the previous finalized period." }),
            unit_decline: item => ({ title: `${item.unit}: CVI down ${Math.abs(item.value).toFixed(1)} points`, detail: "Compared with the previous finalized period." }),
            unit_low: item => ({ title: `${item.unit}: average CVI ${item.value.toFixed(1)}`, detail: "Below the 60-point attention band." }),
            coverage_gap: item => ({ title: `${item.count} of ${item.total} people without a CVI yet`, detail: "Missing evaluations are excluded from averages, not counted as zero." }),
            no_final_result: item => ({ title: `${item.count} of ${item.total} people have no finalized result`, detail: "They were not eligible or had no evaluation when the period was locked." }),
            kpi_no_data: item => ({ title: `${item.count} ${plural(item.count, "indicator has", "indicators have")} no approved data`, detail: "Not included in Collective Contribution until values are approved." }),
            kpi_watch: item => ({ title: `${item.count} ${plural(item.count, "indicator", "indicators")} at 65–84% achievement`, detail: "Worth monitoring." }),
            provisional_incomplete: item => ({ title: item.value == null ? "Evaluations for this period are still in progress" : `Only ${Math.round(item.value)}% of this period’s assessment weight is evaluated`, detail: "Provisional averages reflect the components assessed so far. Changes against finalized periods are not flagged until coverage reaches 80%." }),
            pending_decisions: item => ({ title: `${item.count} ${plural(item.count, "item awaits", "items await")} your decision`, detail: "Contributions and indicator values waiting in Approvals." }),
        },
        open: "Open",
    },
    trend: {
        title: "Performance trend",
        subtitle: "Organization averages by period",
        series: { cvi: "CVI", individual: "Individual", collective: "Collective" },
        provisional: "Provisional",
        final: "Finalized",
        unavailable: "No result",
        failed: "Not loaded",
        empty: "The trend appears once at least one period has results.",
        table: "Trend data",
        period: "Period",
        people: "Results",
        openHistory: "Open history",
    },
    coverage: {
        title: "Evaluation coverage",
        subtitle: "Who is included in this period's results",
        statuses: { final: "Finalized", provisional: "Provisional", partial: "Partially assessed", not_assessed: "Not assessed" },
        assessment: "Average assessment coverage",
        assessmentHelp: "Share of configured component weight with a valid evaluation (provisional periods only).",
        distribution: "CVI distribution",
        bands: { high: "80 and above", acceptable: "60 – 79", attention: "Below 60" },
        none: "No CVI results to distribute yet.",
    },
    breakdown: {
        title: "Contribution breakdown",
        subtitle: "Organization averages of each component; n = people with a value",
        individual: "Individual Contribution",
        collective: "Collective Contribution",
        components: {
            routine: "Core Responsibilities",
            nonRoutine: "Additional Contributions",
            individualGoals: "Individual goals",
            peerFeedback: "Peer feedback",
            team: "Team / local unit",
            upstream: "Parent units",
            organization: "Organization",
        },
        notAssessed: "Not assessed",
        n: n => `n = ${n}`,
    },
    structure: {
        title: "Organizational structure",
        subtitle: "Select a unit to see its people, results and indicators for the selected period.",
        organization: "Organization",
        unit: "Unit",
        types: { organization: "Organization", department: "Department", team: "Team", division: "Division", unit: "Unit" },
        people: n => `${n} ${plural(n, "person", "people")}`,
        nodeLabel: (name, type, n, cvi) => `${name}, ${type}, ${n} ${plural(n, "person", "people")}, CVI ${cvi}`,
        panelLabel: name => `Summary for ${name}`,
        manager: "Unit manager",
        noManager: "Not assigned",
        members: "Members (incl. sub-units)",
        basis: (n, total) => `${n} of ${total} with a CVI`,
        smallSample: "Fewer than 3 results: treat this average with caution.",
        coverage: "Evaluation coverage",
        noPeople: "No people in this unit",
        indicators: "Unit indicators (engine achievement)",
        noIndicators: "No indicators are assigned to this unit.",
        noData: "No approved data",
        more: n => `+ ${n} more`,
        indicatorBands: (watch, onTrack) => `Below ${watch}% needs attention · ${onTrack}% and above is on track`,
        viewDetails: "View details",
        detailsNote: "Opens the unit dashboard for the active period.",
    },
    units: {
        title: "Business units",
        subtitle: "Top-level units, including all sub-units",
        unit: "Unit",
        people: "People",
        cvi: "CVI",
        individual: "Indiv.",
        collective: "Coll.",
        change: "Change",
        empty: "No organizational units are configured.",
        smallSample: "Too few results to compare",
        open: name => `Open ${name}`,
        coverage: (n, total) => `${n}/${total} with CVI`,
        subunits: n => `${n} sub-${plural(n, "unit", "units")}`,
        note: "Units use the current organization structure. Change compares with the previous finalized period when both have at least 3 results.",
    },
    indicators: {
        title: "Strategic indicators",
        subtitle: "Achievement calculated by the CVI engine from approved values",
        statuses: { critical: "Below 65%", watch: "65–84%", on_track: "On track", no_data: "No approved data" },
        scopes: { organization: "Organization", upstream: "Unit", team: "Team" },
        actual: "Actual",
        target: "Target",
        empty: "No strategic indicators are configured for this period.",
        showAll: n => `Show all ${n}`,
        showLess: "Show fewer",
    },
    actions: { inbox: "Approvals", history: "History", members: "People" },
    footnote: "Organization and unit values are averages of available individual results. Missing evaluations are shown as “—” and are never counted as zero.",
};

const FR = {
    eyebrow: "Vue de direction",
    title: "Performance de l’organisation",
    subtitle: (people, units) => `${people} personnes dans le périmètre · ${units} ${plural(units, "unité", "unités")} de premier niveau`,
    periodLabel: "Période d’évaluation",
    periodStates: { final: "Finalisée", provisional: "En cours", draft: "Non commencée" },
    banner: {
        provisionalTitle: "Résultats provisoires",
        provisional: (withCvi, total) => `Cette période est encore ouverte. ${withCvi} personnes sur ${total} ont actuellement un CVI provisoire; les valeurs évolueront au fil des évaluations et ne seront définitives qu’au verrouillage de la période.`,
        finalTitle: "Résultats finalisés",
        final: version => `Les valeurs proviennent des instantanés finaux enregistrés au verrouillage de la période${version ? ` (formule ${version})` : ""}. Elles ne sont pas recalculées.`,
        errorTitle: "Impossible de charger les résultats",
        error: "Le service CVI n’a pas répondu pour cette période. Aucune valeur n’est affichée plutôt que des valeurs incomplètes.",
        forbidden: "Votre compte n’est pas autorisé à consulter les résultats de l’ensemble de l’organisation.",
        retry: "Réessayer",
        loading: "Chargement des résultats de l’organisation…",
    },
    hero: {
        title: "CVI moyen",
        caption: "Moyenne des résultats CVI individuels disponibles",
        basis: (n, total) => `Basé sur ${n} personnes sur ${total}`,
        noScore: "Non disponible",
        noScoreHelp: "Aucune personne du périmètre n’a encore de résultat CVI pour cette période. Un résultat apparaît lorsque les deux dimensions de contribution ont été évaluées.",
        vs: label => `par rapport à ${label} (finalisé)`,
        noBaseline: "Aucune période finalisée antérieure à comparer",
        individual: "Contribution individuelle",
        individualHelp: "Responsabilités, activités, objectifs et rétroaction des collègues",
        collective: "Contribution collective",
        collectiveHelp: "Indicateurs d’équipe, d’unité et d’organisation",
        formula: "Le CVI de chaque personne est la moyenne géométrique de sa contribution individuelle et de sa contribution collective, selon les pondérations configurées pour son poste.",
        provisionalComparison: "Valeur provisoire comparée à une valeur finalisée.",
        comparisonWithheld: "La comparaison avec les périodes finalisées s’affiche lorsqu’au moins 80\u00a0% de la pondération d’évaluation de cette période a été évaluée.",
        cycles: "CVI moyen par période",
    },
    attention: {
        title: "Points d’attention de la direction",
        subtitle: "Priorisés à partir des résultats de la période",
        empty: "Aucun point ne requiert l’attention de la direction pour cette période.",
        severities: { critical: "Priorité", warning: "À examiner", info: "Note" },
        items: {
            load_failed: () => ({ title: "Résultats non disponibles", detail: "Le service CVI n’a pas pu fournir les résultats de cette période." }),
            kpi_critical: item => ({ title: `${item.count} ${plural(item.count, "indicateur stratégique", "indicateurs stratégiques")} sous 65\u00a0% d’atteinte`, detail: item.names.join(" · ") }),
            org_decline: item => ({ title: `CVI moyen en baisse de ${Math.abs(item.value).toFixed(1).replace(".", ",")} points`, detail: "Par rapport à la période finalisée précédente." }),
            org_improve: item => ({ title: `CVI moyen en hausse de ${item.value.toFixed(1).replace(".", ",")} points`, detail: "Par rapport à la période finalisée précédente." }),
            unit_decline: item => ({ title: `${item.unit} : CVI en baisse de ${Math.abs(item.value).toFixed(1).replace(".", ",")} points`, detail: "Par rapport à la période finalisée précédente." }),
            unit_low: item => ({ title: `${item.unit} : CVI moyen de ${item.value.toFixed(1).replace(".", ",")}`, detail: "Sous le seuil d’attention de 60 points." }),
            coverage_gap: item => ({ title: `${item.count} personnes sur ${item.total} sans CVI pour l’instant`, detail: "Les évaluations manquantes sont exclues des moyennes, jamais comptées comme zéro." }),
            no_final_result: item => ({ title: `${item.count} personnes sur ${item.total} sans résultat finalisé`, detail: "Elles n’étaient pas admissibles ou n’avaient aucune évaluation au verrouillage de la période." }),
            kpi_no_data: item => ({ title: `${item.count} ${plural(item.count, "indicateur sans", "indicateurs sans")} données approuvées`, detail: "Exclus de la contribution collective jusqu’à l’approbation des valeurs." }),
            kpi_watch: item => ({ title: `${item.count} ${plural(item.count, "indicateur", "indicateurs")} entre 65\u00a0% et 84\u00a0% d’atteinte`, detail: "À surveiller." }),
            provisional_incomplete: item => ({ title: item.value == null ? "Les évaluations de cette période sont en cours" : `Seulement ${Math.round(item.value)}\u00a0% de la pondération d’évaluation de cette période est évaluée`, detail: "Les moyennes provisoires reflètent les composantes évaluées jusqu’ici. Les écarts par rapport aux périodes finalisées ne sont signalés qu’à partir de 80\u00a0% de couverture." }),
            pending_decisions: item => ({ title: `${item.count} ${plural(item.count, "élément attend", "éléments attendent")} votre décision`, detail: "Contributions et valeurs d’indicateurs en attente dans Approbations." }),
        },
        open: "Ouvrir",
    },
    trend: {
        title: "Évolution de la performance",
        subtitle: "Moyennes de l’organisation par période",
        series: { cvi: "CVI", individual: "Individuelle", collective: "Collective" },
        provisional: "Provisoire",
        final: "Finalisé",
        unavailable: "Aucun résultat",
        failed: "Non chargé",
        empty: "L’évolution s’affiche dès qu’au moins une période a des résultats.",
        table: "Données de l’évolution",
        period: "Période",
        people: "Résultats",
        openHistory: "Ouvrir l’historique",
    },
    coverage: {
        title: "Couverture des évaluations",
        subtitle: "Personnes incluses dans les résultats de la période",
        statuses: { final: "Finalisé", provisional: "Provisoire", partial: "Partiellement évalué", not_assessed: "Non évalué" },
        assessment: "Couverture moyenne des évaluations",
        assessmentHelp: "Part de la pondération configurée disposant d’une évaluation valide (périodes provisoires seulement).",
        distribution: "Répartition du CVI",
        bands: { high: "80 et plus", acceptable: "60 – 79", attention: "Moins de 60" },
        none: "Aucun résultat CVI à répartir pour l’instant.",
    },
    breakdown: {
        title: "Détail des contributions",
        subtitle: "Moyennes de l’organisation par composante; n = personnes ayant une valeur",
        individual: "Contribution individuelle",
        collective: "Contribution collective",
        components: {
            routine: "Responsabilités principales",
            nonRoutine: "Contributions additionnelles",
            individualGoals: "Objectifs individuels",
            peerFeedback: "Rétroaction des collègues",
            team: "Équipe / unité locale",
            upstream: "Unités parentes",
            organization: "Organisation",
        },
        notAssessed: "Non évalué",
        n: n => `n = ${n}`,
    },
    structure: {
        title: "Structure organisationnelle",
        subtitle: "Sélectionnez une unité pour voir ses membres, ses résultats et ses indicateurs pour la période choisie.",
        organization: "Organisation",
        unit: "Unité",
        types: { organization: "Organisation", department: "Service", team: "Équipe", division: "Division", unit: "Unité" },
        people: n => `${n} ${plural(n, "personne", "personnes")}`,
        nodeLabel: (name, type, n, cvi) => `${name}, ${type}, ${n} ${plural(n, "personne", "personnes")}, CVI ${cvi}`,
        panelLabel: name => `Résumé pour ${name}`,
        manager: "Gestionnaire de l’unité",
        noManager: "Non attribué",
        members: "Membres (sous-unités incluses)",
        basis: (n, total) => `${n} sur ${total} avec un CVI`,
        smallSample: "Moins de 3 résultats : interprétez cette moyenne avec prudence.",
        coverage: "Couverture des évaluations",
        noPeople: "Aucune personne dans cette unité",
        indicators: "Indicateurs de l’unité (atteinte calculée par le moteur)",
        noIndicators: "Aucun indicateur n’est attribué à cette unité.",
        noData: "Aucune donnée approuvée",
        more: n => `+ ${n} autres`,
        indicatorBands: (watch, onTrack) => `Moins de ${watch}\u00a0% : à surveiller · ${onTrack}\u00a0% et plus : conforme`,
        viewDetails: "Voir les détails",
        detailsNote: "Ouvre le tableau de bord de l’unité pour la période active.",
    },
    units: {
        title: "Unités d’affaires",
        subtitle: "Unités de premier niveau, sous-unités comprises",
        unit: "Unité",
        people: "Personnes",
        cvi: "CVI",
        individual: "Indiv.",
        collective: "Coll.",
        change: "Écart",
        empty: "Aucune unité organisationnelle n’est configurée.",
        smallSample: "Trop peu de résultats pour comparer",
        open: name => `Ouvrir ${name}`,
        coverage: (n, total) => `${n}/${total} avec CVI`,
        subunits: n => `${n} ${plural(n, "sous-unité", "sous-unités")}`,
        note: "Les unités suivent la structure organisationnelle actuelle. L’écart est calculé par rapport à la période finalisée précédente lorsque les deux comptent au moins 3 résultats.",
    },
    indicators: {
        title: "Indicateurs stratégiques",
        subtitle: "Atteinte calculée par le moteur CVI à partir des valeurs approuvées",
        statuses: { critical: "Sous 65\u00a0%", watch: "65 à 84\u00a0%", on_track: "En bonne voie", no_data: "Aucune donnée approuvée" },
        scopes: { organization: "Organisation", upstream: "Unité", team: "Équipe" },
        actual: "Réel",
        target: "Cible",
        empty: "Aucun indicateur stratégique n’est configuré pour cette période.",
        showAll: n => `Afficher les ${n}`,
        showLess: "Afficher moins",
    },
    actions: { inbox: "Approbations", history: "Historique", members: "Personnes" },
    footnote: "Les valeurs de l’organisation et des unités sont des moyennes des résultats individuels disponibles. Les évaluations manquantes sont affichées « — » et ne sont jamais comptées comme zéro.",
};

export function executiveCopy(language) {
    return language === "fr" ? FR : EN;
}
